import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { ConfigurationTab } from '@/components/settings/accounting/ConfigurationTab';
import { ConflictsTab } from '@/components/settings/accounting/ConflictsTab';
import { ConnectionTab } from '@/components/settings/accounting/ConnectionTab';
import { FailuresTab } from '@/components/settings/accounting/FailuresTab';
import { HealthTab } from '@/components/settings/accounting/HealthTab';
import { ManualSyncTab } from '@/components/settings/accounting/ManualSyncTab';
import { ReconciliationTab } from '@/components/settings/accounting/ReconciliationTab';
import { DeadLetterTab } from '@/components/settings/accounting/DeadLetterTab';
import { PurchasingAccountingTab } from '@/features/purchasing/accounting/PurchasingAccountingTab';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';
import { settingsKeys } from '@/lib/queryKeys';
import type { AccountingEntityType } from '@/types/accounting-sync';

const TABS = [
  'configuration',
  'connection',
  'health',
  'reconciliation',
  'dead-letter',
  'failures',
  'conflicts',
  'manual',
  'purchasing',
] as const;
type TopTab = (typeof TABS)[number];

const STATUS_FILTERS = ['all', 'pending', 'synced', 'failed', 'unsynced'] as const;
type ManualStatusFilter = (typeof STATUS_FILTERS)[number];

const ENTITY_TYPES: ReadonlyArray<AccountingEntityType> = [
  'contact',
  'invoice',
  'payment',
  'estimate',
  'deposit',
  'expense',
  'membership_charge',
  'credit_memo',
  'change_order',
  'item',
  'vendor',
  'supplier_bill',
  'purchase_order',
  'inventory_journal',
];

interface AccountingSearch {
  /** Top-level tab. Defaults to 'configuration' when omitted. */
  tab?: TopTab;
  /** Manual Sync — active entity sub-tab. */
  entity?: AccountingEntityType;
  /** Manual Sync — push (Notifi → QB) or pull (QB → Notifi). */
  direction?: 'push' | 'pull';
  /** Manual Sync — status filter chip. */
  manualStatus?: ManualStatusFilter;
  /**
   * Set by the QB OAuth callback redirect so the FE can invalidate the
   * cached quickBooksStatus query and show a confirmation. Stripped
   * from the URL after we've handled it.
   */
  qb?: 'connected' | 'error';
  /** Free-form failure reason when `qb=error`. */
  reason?: string;
}

function asTab(v: unknown): TopTab | undefined {
  return typeof v === 'string' && (TABS as readonly string[]).includes(v)
    ? (v as TopTab)
    : undefined;
}
function asEntity(v: unknown): AccountingEntityType | undefined {
  return typeof v === 'string' && (ENTITY_TYPES as readonly string[]).includes(v)
    ? (v as AccountingEntityType)
    : undefined;
}
function asDirection(v: unknown): 'push' | 'pull' | undefined {
  return v === 'push' || v === 'pull' ? v : undefined;
}
function asManualStatus(v: unknown): ManualStatusFilter | undefined {
  return typeof v === 'string' && (STATUS_FILTERS as readonly string[]).includes(v)
    ? (v as ManualStatusFilter)
    : undefined;
}
function asQbOutcome(v: unknown): 'connected' | 'error' | undefined {
  return v === 'connected' || v === 'error' ? v : undefined;
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/accounting')({
  validateSearch: (raw: Record<string, unknown>): AccountingSearch => ({
    tab: asTab(raw.tab),
    entity: asEntity(raw.entity),
    direction: asDirection(raw.direction),
    manualStatus: asManualStatus(raw.manualStatus),
    qb: asQbOutcome(raw.qb),
    reason: typeof raw.reason === 'string' ? raw.reason : undefined,
  }),
  component: AccountingSettingsPage,
});

function AccountingSettingsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const purchasingEnabled = usePurchasingEnabled();

  // Handle the QB OAuth callback redirect. The backend redirects to
  // `/dashboard/settings/accounting?qb=connected` after a successful
  // OAuth exchange (or `?qb=error&reason=…` on failure). The route
  // cache from before the user left for Intuit still says
  // `connected: false`, so without an explicit invalidate the FE keeps
  // showing the old state until the next refetch trigger fires.
  useEffect(() => {
    if (!search.qb) return;
    if (search.qb === 'connected') {
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.quickbooks(),
      });
      // 6s instead of Sonner's default ~4s — operators returning from an
      // OAuth round-trip benefit from a confirmation that lingers long
      // enough to actually read while their attention is shifting back.
      toast.success('QuickBooks connected', {
        description: 'Your accounting provider is now linked.',
        duration: 6000,
      });
    } else {
      const reason = search.reason ? `: ${search.reason}` : '';
      toast.error('QuickBooks connection failed', {
        description: `Try connecting again${reason}.`,
        duration: 8000,
      });
    }
    // Strip the OAuth params from the URL so a refresh doesn't re-fire
    // the toast / re-invalidate.
    void navigate({
      search: (prev) => ({ ...prev, qb: undefined, reason: undefined }),
      replace: true,
    });
  }, [search.qb, search.reason, queryClient, navigate]);

  // The Purchasing tab only where purchasing is on (PLAT-FF-1); a link to it
  // opens Configuration otherwise.
  const activeTab =
    search.tab === 'purchasing' && !purchasingEnabled
      ? 'configuration'
      : (search.tab ?? 'configuration');

  return (
    <div className="max-w-6xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Accounting Integration</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sync invoices, payments, deposits, and more with your accounting system.
        </p>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          const next = asTab(v) ?? 'configuration';
          // Drop manual-sync sub-state when leaving the Manual tab so the URL
          // doesn't carry stale params; preserve it when reentering.
          void navigate({
            search: (prev) => ({
              ...prev,
              // Drop ?tab=configuration from the URL since it's the default —
              // keeps deep-links to other tabs explicit.
              tab: next === 'configuration' ? undefined : next,
              ...(next === 'manual'
                ? {}
                : { entity: undefined, direction: undefined, manualStatus: undefined }),
            }),
            replace: true,
          });
        }}
      >
        <TabsList>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="connection">Connection</TabsTrigger>
          <TabsTrigger value="health">Health</TabsTrigger>
          <TabsTrigger value="reconciliation">Reconciliation</TabsTrigger>
          <TabsTrigger value="dead-letter">Dead-Letter</TabsTrigger>
          <TabsTrigger value="failures">Failures</TabsTrigger>
          <TabsTrigger value="conflicts">Conflicts</TabsTrigger>
          <TabsTrigger value="manual">Manual Sync</TabsTrigger>
          {purchasingEnabled && <TabsTrigger value="purchasing">Purchasing</TabsTrigger>}
        </TabsList>
        {purchasingEnabled && (
          <TabsContent value="purchasing">
            <PurchasingAccountingTab />
          </TabsContent>
        )}
        <TabsContent value="connection">
          <ConnectionTab />
        </TabsContent>
        <TabsContent value="health">
          <HealthTab />
        </TabsContent>
        <TabsContent value="reconciliation">
          <ReconciliationTab />
        </TabsContent>
        <TabsContent value="dead-letter">
          <DeadLetterTab />
        </TabsContent>
        <TabsContent value="failures">
          <FailuresTab />
        </TabsContent>
        <TabsContent value="conflicts">
          <ConflictsTab />
        </TabsContent>
        <TabsContent value="manual">
          <ManualSyncTab
            activeEntity={search.entity ?? null}
            activeDirection={search.direction ?? null}
            activeStatus={search.manualStatus ?? null}
            onChangeEntity={(entity) =>
              void navigate({
                search: (prev) => ({ ...prev, entity }),
                replace: true,
              })
            }
            onChangeDirection={(direction) =>
              void navigate({
                search: (prev) => ({ ...prev, direction }),
                replace: true,
              })
            }
            onChangeStatus={(manualStatus) =>
              void navigate({
                search: (prev) => ({ ...prev, manualStatus }),
                replace: true,
              })
            }
          />
        </TabsContent>
        <TabsContent value="configuration">
          <ConfigurationTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
